import { clsx } from 'clsx';
import Icon from 'components/common/Icon';
import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth';
import { useChatSupport } from 'contexts/chatSupport';
import { getLastDashboardRoute } from '../../util/routes';
import Logo from '../common/Logo';
import LogoCollapsed from '../common/LogoCollapsed';
import Tooltip from '../common/Tooltip';
import UserBubble from '../common/UserBubble';
import { useHasRole, FeatureFlagName, useHasFeatureFlag } from 'hooks/users';

const NewLayout = ({
  canExpandSidebar = true,
  children,
  header = undefined,
}: {
  canExpandSidebar?: boolean;
  children: ReactNode;
  header?: ReactNode;
}): JSX.Element => {
  const [isSidebarCollapsed] = useState(!canExpandSidebar);

  return (
    <div className="relative flex flex-col h-full w-full font-inter text-sm text-gray-900 font-normal">
      <div
        className={clsx('fixed top-0 left-0 bg-gray-50', {
          'w-main-sidebar-collapsed': isSidebarCollapsed,
          'w-main-sidebar xl:w-main-sidebar-collapsed': !isSidebarCollapsed,
        })}
      >
        <Sidebar isCollapsed={isSidebarCollapsed} />
      </div>

      <div
        className={clsx('flex-grow h-px', {
          'ml-main-sidebar-collapsed': isSidebarCollapsed,
          'ml-main-sidebar xl:ml-main-sidebar-collapsed': !isSidebarCollapsed,
          'mt-14': header,
        })}
      >
        {header && (
          <div className="fixed top-0 h-14 w-full border-b border-gray-300 z-20">
            {header}
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

export default NewLayout;

export const Sidebar = ({
  isCollapsed,
}: {
  isCollapsed: boolean;
}): JSX.Element => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showChat } = useChatSupport();
  const isAdmin = useHasRole('admin');

  const showDeveloperPage = useHasFeatureFlag(FeatureFlagName.DEVELOPER_PAGE);
  const showBillingPage = useHasFeatureFlag(FeatureFlagName.BILLING_PAGE);

  return (
    <div
      className={clsx(
        'flex flex-col w-full h-screen p-6 border-r border-light-grey',
        {
          'pt-0 px-2': isCollapsed,
          'xl:px-2': !isCollapsed,
        },
      )}
    >
      <SidebarLogo isCollapsed={isCollapsed} />
      <div className="mt-4 flex flex-1 flex-col justify-between divide-light-grey text-dark-grey">
        <ul className="cursor-pointer">
          <SidebarLink
            icon={<Icon id="copy-06" />}
            isActive={location.pathname.startsWith('/dashboard')}
            isCollapsed={isCollapsed}
            label="Your Surveys"
            onClick={() => {
              navigate(getLastDashboardRoute());
            }}
          />
          {showBillingPage && (
            <SidebarLink
              icon={<Icon id="billing" />}
              isActive={location.pathname.startsWith('/billing')}
              isCollapsed={isCollapsed}
              label="Billing"
              onClick={() => {
                navigate('/billing');
              }}
            />
          )}
          <SidebarLink
            icon={<Icon id="users-01" />}
            isActive={location.pathname.startsWith('/team')}
            isCollapsed={isCollapsed}
            label="Your Team"
            onClick={() => {
              navigate('/team');
            }}
          />
          {isAdmin && (
            <SidebarLink
              icon={<Icon id="settings-01" />}
              isActive={location.pathname.startsWith('/admin')}
              isCollapsed={isCollapsed}
              label="Admin"
              onClick={() => {
                navigate('/admin');
              }}
            />
          )}
          {showDeveloperPage && (
            <SidebarLink
              icon={<Icon id="rocket" />}
              isActive={location.pathname.startsWith('/developer')}
              isCollapsed={isCollapsed}
              label="Developer"
              onClick={() => {
                navigate('/developer');
              }}
            />
          )}
        </ul>
        <ul className="mt-4 pt-4 cursor-pointer">
          <SidebarLink
            icon={<Icon id="help-circle" />}
            isCollapsed={isCollapsed}
            label="Help Center"
            target="_blank"
            to="https://help.useglass.com"
          />
          <SidebarLink
            icon={<Icon id="message-chat-circle" />}
            isCollapsed={isCollapsed}
            label="Chat with us"
            onClick={showChat}
          />
          <SidebarLink
            icon={<Icon id="log-out-01" />}
            isCollapsed={isCollapsed}
            label="Log Out"
            onClick={() => logout()}
          />
        </ul>
      </div>
      {user && (
        <div
          className="pt-3.5 pl-2 flex items-center text-sm cursor-pointer space-x-4"
          onClick={() => {
            navigate('/account');
          }}
        >
          <div className="shrink-0">
            <UserBubble user={user} withTooltip={false} />
          </div>

          <div
            className={clsx('flex flex-col leading-none', {
              hidden: isCollapsed,
              'inline xl:hidden': !isCollapsed,
            })}
          >
            {user.firstName} {user.lastName}
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarLogo = ({
  isCollapsed,
}: {
  isCollapsed: boolean;
}): JSX.Element => {
  return (
    <div>
      <div
        className={clsx('flex items-center h-14 justify-center', {
          flex: isCollapsed,
          'hidden xl:flex': !isCollapsed,
        })}
      >
        <LogoCollapsed />
      </div>
      <div
        className={clsx('justify-center mb-8', {
          hidden: isCollapsed,
          'flex xl:hidden': !isCollapsed,
        })}
      >
        <div className="w-3/4">
          <Logo />
        </div>
      </div>
    </div>
  );
};

const SidebarLink = ({
  icon,
  isActive = false,
  isCollapsed,
  label,
  onClick,
  thirdPartyClassName = '',
  to,
  target,
}: {
  icon: ReactNode;
  isActive?: boolean;
  isCollapsed: boolean;
  label: string;
  onClick?: () => void;
  thirdPartyClassName?: string;
  to?: string;
  target?: React.HTMLAttributeAnchorTarget;
}): JSX.Element => {
  const className = clsx(
    'flex items-center px-4 py-3.5 cursor-pointer',
    thirdPartyClassName,
    {
      'text-forest font-semibold': isActive,
      'hover:bg-dark-white font-medium': !isActive,
      'justify-center': isCollapsed,
    },
  );
  const linkContent = (
    <>
      <div className="w-4 h-4 flex items-center justify-center">{icon}</div>
      <span
        className={clsx('ml-4', {
          hidden: isCollapsed,
          'inline xl:hidden': !isCollapsed,
        })}
      >
        {label}
      </span>
    </>
  );

  const sidebarLink = to ? (
    <Link className={className} target={target} to={to}>
      {linkContent}
    </Link>
  ) : (
    <a className={className} onClick={onClick}>
      {linkContent}
    </a>
  );

  return isCollapsed ? (
    <Tooltip placement="right" trigger={sidebarLink}>
      {label}
    </Tooltip>
  ) : (
    sidebarLink
  );
};
